import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { components as c } from "@/components";
import { hooks as h } from "@/hooks";
import { Alert, AlertDescription } from "@patient-watch/ui/alert";
import Stepper from "@/components/common/Stepper";
import useUsersOrganisationsRolesQuery from "@/features/auth/api/selectUsersOrganisationsRoles";
import { uniqueOrganisationsFromRoles } from "@/features/auth/utils/uniqueOrganisationsFromRoles";
import useSelectOrganisationById from "@/features/auth/api/selectOrganisationById";
import {
    type DiaryInviteSearchT,
    getDiaryInviteContext,
    getDiaryInviteOrigin,
} from "@/features/invite/utils/diaryInviteContext";
import { getDiaryInviteSteps, parseDiaryInviteMethod } from "@/features/invite/utils/diaryInviteSteps";
import { OrganisationComboBox } from "@/features/organisations/components/OrganisationComboBox";
import { InviteMethodToggle } from "@/features/invite/components/InviteMethodToggle";
import ContactPersonComboBox from "@/features/shared_attributes/components/ContactPersonComboBox";
import { useApplyDefaultInviteContact } from "@/features/invite/useApplyDefaultInviteContact";
import { normalizeTemplateSettingsSearch } from "@/features/templates/utils/normalizeTemplateSettingsSearch";
import { effectiveRequireHospitalNumber } from "@/features/diaries/utils/requireHospitalNumber";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useInviteCopy } from "@/features/i18n/inviteCopy";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { ReferenceDateModeToggle } from "@/features/templates/components/referenceDate/ReferenceDateModeToggle";
import { effectiveReferenceDateMode } from "@/features/templates/utils/referenceDateMode";
import { saveDiaryInviteDraft } from "@/features/invite/utils/diaryInviteDraft";
import { templateSettingsSchema, type TemplateSettingsSchemaT } from "@patient-watch/types";

export const Route = createFileRoute("/_app/invite/diary/organisation")({
    component: InviteOrganisationPage,
    validateSearch: (search: Record<string, unknown>) => search as DiaryInviteSearchT,
});

function InviteOrganisationPage() {
    const search = Route.useSearch();
    const { auth } = Route.useRouteContext();
    const navigate = useNavigate();
    const { locale: appLocale } = useAppLocale();
    const inviteCopy = useInviteCopy();
    const patientsCopy = usePatientsCopy();
    const templatesCopy = useTemplatesCopy();

    const origin = getDiaryInviteOrigin(search);
    const [inviteMethod, setInviteMethod] = useState(() => parseDiaryInviteMethod(search.invite_method) ?? "email");
    const context = getDiaryInviteContext(origin, {
        patients: patientsCopy.patients,
        templates: templatesCopy.templates,
        newDiary: patientsCopy.newDiary,
    });

    h.useNav([
        { label: context.rootLabel, route: { to: context.rootTo } },
        { label: context.flowLabel, route: { to: "/invite/diary/review", search } },
        { label: inviteCopy.organisationAndContact, route: { to: "/invite/diary/organisation", search } },
    ]);

    const { data: userOrgRoles = [], isLoading: isLoadingOrgs } = useUsersOrganisationsRolesQuery(auth.id);
    const hasTemplateSettings = search && (Boolean(search.trackers?.length) || Boolean(search.required_events?.length));
    const organisations = useMemo(() => uniqueOrganisationsFromRoles(userOrgRoles), [userOrgRoles]);
    const sendRoute = inviteMethod === "qr" ? "/invite/diary/qr" : "/invite/diary/patient";

    const form = useForm<TemplateSettingsSchemaT>({
        resolver: zodResolver(templateSettingsSchema),
        defaultValues: normalizeTemplateSettingsSearch({
            ...search,
            quickstart_key: search.quickstart_key || "PAIN",
            locale: search.locale ?? appLocale,
        }),
    });

    const organisation_id = form.watch("organisation_id");
    const contact_id = form.watch("contact_id");
    useApplyDefaultInviteContact(form, organisation_id);
    const formValues = form.watch();

    const { data: selectedOrgData } = useSelectOrganisationById(organisation_id ?? undefined);
    const isNHS = selectedOrgData?.nhs_flag === true;
    const requireHospitalNumber = effectiveRequireHospitalNumber(formValues.require_hospital_number, isNHS);

    useEffect(() => {
        const organizationName =
            userOrgRoles.find((role) => role.organisation_id === organisation_id)?.organisation_name ?? "Not selected";

        form.setValue("organisation_name", organizationName);
    }, [organisation_id, userOrgRoles, form]);

    useEffect(() => {
        if (organisations.length === 1 && !organisation_id && organisations[0]) {
            form.setValue("organisation_id", organisations[0].organisation_id);
        }
    }, [organisations, organisation_id, form]);

    const showHospitalNumber = requireHospitalNumber && Boolean(organisation_id);
    const showContactPicker = Boolean(organisation_id);
    const canContinue = Boolean(organisation_id) && Boolean(contact_id) && Boolean(hasTemplateSettings);

    const continueToSend = () => {
        if (!canContinue) return;
        const next = { ...search, ...form.getValues(), origin, invite_method: inviteMethod };
        saveDiaryInviteDraft(next);
        void navigate({ to: sendRoute, search: next });
    };

    return (
        <div className="space-y-4">
            {!isLoadingOrgs ? (
                <Stepper
                    steps={getDiaryInviteSteps("organisation", {
                        organisationCount: organisations.length,
                        inviteMethod,
                        copy: inviteCopy,
                    })}
                />
            ) : null}

            {!hasTemplateSettings && (
                <Alert variant="destructive" className="bg-red-50 border-red-200">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <AlertDescription className="text-red-800">
                        {inviteCopy.noTemplateSelected}{" "}
                        <button
                            type="button"
                            onClick={() => void navigate({ to: "/templates" } as never)}
                            className="font-medium underline"
                        >
                            {inviteCopy.createTemplate}
                        </button>{" "}
                        {inviteCopy.createTemplateFirst}
                    </AlertDescription>
                </Alert>
            )}

            <c.ui.Form {...form}>
                <div className="space-y-4">
                    <OrganisationComboBox organisations={organisations} />

                    {showContactPicker ? (
                        <ContactPersonComboBox organisationId={organisation_id} warnIfPersonalEmail />
                    ) : null}

                    {organisation_id ? (
                        <ReferenceDateModeToggle
                            value={effectiveReferenceDateMode(formValues.reference_date_mode)}
                            onChange={(mode) => form.setValue("reference_date_mode", mode, { shouldDirty: true })}
                        />
                    ) : null}

                    {organisation_id ? <InviteMethodToggle value={inviteMethod} onChange={setInviteMethod} /> : null}

                    {showHospitalNumber ? (
                        <c.fc.Input
                            name="hospital_number"
                            label={inviteCopy.hospitalNumberOptional}
                            placeholder={inviteCopy.enterNhsHospitalNumber}
                            description={inviteCopy.hospitalNumberDescription}
                        />
                    ) : null}

                    {!organisation_id ? (
                        <p className="text-muted-foreground text-sm">{inviteCopy.selectOrganisationToContinue}</p>
                    ) : organisation_id && !contact_id ? (
                        <p className="text-muted-foreground text-sm">{inviteCopy.selectContactToContinue}</p>
                    ) : null}

                    <div className="pt-4">
                        <fc.NextButton disabled={!canContinue} onClick={continueToSend} />
                    </div>
                </div>
            </c.ui.Form>
        </div>
    );
}
